import React from "react";
import {
  SuggestionPrimitive,
  ThreadPrimitive,
  useThread,
  useThreadRuntime,
} from "@assistant-ui/react";
import { useChatSelections } from "./ChatSelectionContext";
import { AssistantMessage, UserMessage } from "./ChatMessageRenderer";
import {
  mapAdditionalResponsesFromMessageMetadata,
  toAssistantAppendMessage,
} from "./mock/mockEngine";
import classes from "./Chat.module.css";

const FollowUpSuggestion = () => {
  return (
    <SuggestionPrimitive.Trigger send className={classes.followUpSuggestion}>
      <SuggestionPrimitive.Title />
    </SuggestionPrimitive.Trigger>
  );
};

const ChatThread = () => {
  const thread = useThread();
  const threadRuntime = useThreadRuntime();
  const { scrollToBottomRequestId } = useChatSelections();
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = React.useRef(true);
  const processedAdditionalResponsesByMessageIdRef = React.useRef<Set<string>>(
    new Set()
  );

  const isEmpty = thread.messages.length === 0;

  const updateStickiness = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const distanceFromBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;

    shouldStickToBottomRef.current = distanceFromBottom <= 24;
  }, []);

  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = "auto") => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior,
    });
  }, []);

  React.useEffect(() => {
    if (thread.isRunning) {
      return;
    }

    const existingMessageIds = new Set(thread.messages.map((message) => message.id));
    const processedMessageIds = processedAdditionalResponsesByMessageIdRef.current;
    processedMessageIds.forEach((messageId) => {
      if (!existingMessageIds.has(messageId)) {
        processedMessageIds.delete(messageId);
      }
    });

    for (const message of thread.messages) {
      if (message.role !== "assistant") {
        continue;
      }

      if (processedMessageIds.has(message.id)) {
        continue;
      }

      const additionalResponses = mapAdditionalResponsesFromMessageMetadata(
        message.metadata.custom?.mockAdditionalResponses
      );
      if (additionalResponses.length === 0) {
        continue;
      }

      processedMessageIds.add(message.id);
      additionalResponses.forEach((additionalResponse) => {
        threadRuntime.append(toAssistantAppendMessage(additionalResponse));
      });
    }
  }, [thread.isRunning, thread.messages, threadRuntime]);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const handleScroll = () => {
      updateStickiness();
    };

    updateStickiness();
    viewport.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
    };
  }, [updateStickiness]);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    let frameId = 0;

    const scheduleScrollToBottom = () => {
      if (!shouldStickToBottomRef.current) {
        return;
      }

      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        scrollToBottom("auto");
      });
    };

    const observer = new MutationObserver(() => {
      scheduleScrollToBottom();
    });

    observer.observe(viewport, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    scheduleScrollToBottom();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, [scrollToBottom]);

  React.useEffect(() => {
    shouldStickToBottomRef.current = true;
    scrollToBottom("auto");
  }, [scrollToBottom, scrollToBottomRequestId]);

  return (
    <ThreadPrimitive.Root className={classes.threadRoot}>
      <ThreadPrimitive.Viewport
        ref={viewportRef}
        className={classes.threadViewport}
        autoScroll={false}
        scrollToBottomOnInitialize={false}
        scrollToBottomOnRunStart={false}
        scrollToBottomOnThreadSwitch={false}
      >
        <ThreadPrimitive.Messages
          components={{
            UserMessage,
            AssistantMessage,
          }}
        />

        {!isEmpty && (
          <div className={classes.followUpContainer}>
            <ThreadPrimitive.Suggestions
              components={{
                Suggestion: FollowUpSuggestion,
              }}
            />
          </div>
        )}
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

export default ChatThread;
